import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/store";
import { setHasReports } from "store/slices/reportsSlice";
import { useTranslation } from "react-i18next";
import PageContainer from "components/htmlElements/PageContainer";
import ReportsBase from "components/Reports/ReportsBase";
import {
  useGetReportsListDtoQuery,
  useGetStatusesQuery,
} from "features/api/reports/reportsSlice";
import Loading from "components/Loading/Loading";
import { ReportStatus } from "models/ReportStatus";
import { useIsFollowup } from "hooks/useIsFollwup";
import { useRole } from "hooks/useRole";
import { UserRole } from "components/Reports/reportAssets/assets";
import { LocalStorageKey, useGetUserData } from "common/helpers/userData";
import {
  firstDate,
  thirtyDaysAgoDate,
  todaysDate,
} from "common/helpers/calculateDays";
import {
  expandedFiltersType,
  filtersType,
  initialFiltersValue,
  reportsPageDataTypes,
} from "./types";
import { getFormattedFilters } from "./utils";

const ReportsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const role = useRole();
  const isFollowUp = useIsFollowup();
  const selectedRole = useGetUserData(LocalStorageKey.SelectedRole)?.value;

  const searchKeyword = useSelector(
    (state: RootState) => state.reportsSliceReducer.searchKeyword
  );
  const pageNumber = useSelector(
    (state: RootState) => state.reportsSliceReducer.pageNumberSearch
  );

  const [expandedFilters, setExpandedFilters] =
    useState<expandedFiltersType | null>(null);
  const [simpleFilters, setSimpleFilters] = useState<filtersType | null>(null);
  const [filters, setFilters] = useState<filtersType>(initialFiltersValue);
  const [isFiltersRun, setIsFiltersRun] = useState<boolean>(false);
  const [checkedReportsIds, setCheckedReportsIds] = useState<number[]>([]);
  const [reportRefetchToggle, setReportRefetchToggle] = useState(false);

  const { data: allStatuses } = useGetStatusesQuery(selectedRole);

  const allStatusIds =
    allStatuses &&
    [...(allStatuses?.followups ?? []), ...(allStatuses?.inProgress ?? [])].map(
      (s) => s.id
    );

  const {
    data: reportsPageData,
    isLoading,
    isFetching,
    isError,
    refetch,
  }: {
    data: reportsPageDataTypes;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  } = useGetReportsListDtoQuery({
    isFollowUpStatuses: searchKeyword ? undefined : isFollowUp,
    role: selectedRole,
    page: pageNumber,
    body: {
      expandedReportsFilters: expandedFilters,
      reportsFilters: {
        ...(simpleFilters ?? {}),
        reportsStatuses: searchKeyword
          ? allStatusIds ?? []
          : simpleFilters?.reportsStatuses ?? [],
      },
      searchInput: searchKeyword,
    },
    isSearchRequest: !!searchKeyword,
  });

  useEffect(() => {
    if (searchKeyword && reportsPageData?.listItems?.length && allStatuses) {
      const reportStatusId = reportsPageData.listItems[0].reportStatus;

      const isStatusFollowUp = allStatuses.followups.some(
        (s) => s.id === reportStatusId
      );
      const isStatusInProgress = allStatuses.inProgress.some(
        (s) => s.id === reportStatusId
      );

      if (isStatusFollowUp && !isFollowUp) {
        navigate("/reports/followup", {
          state: {
            preserveSearch: true,
            searchKeyword,
          },
        });
      } else if (isStatusInProgress && isFollowUp) {
        navigate("/reports/inprogress", {
          state: {
            preserveSearch: true,
            searchKeyword,
          },
        });
      }
    }
  }, [searchKeyword, reportsPageData, allStatuses, isFollowUp, navigate]);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.preserveSearch && location.state?.searchKeyword) {
      dispatch({
        type: "reportsData/setSearchKeyword",
        payload: location.state.searchKeyword,
      });
    }
  }, [location, dispatch]);

  useEffect(() => {
    if (reportsPageData?.listItems) {
      dispatch(setHasReports(reportsPageData.listItems.length > 0));
    }
  }, [reportsPageData, dispatch]);

  let statuses: ReportStatus[] | null = null;
  if (allStatuses) {
    statuses = isFollowUp ? allStatuses.followups : allStatuses.inProgress;
  }

  const handleReportRefetch = () => {
    setReportRefetchToggle((prev) => !prev); // Toggles the reportRefetchToggle state
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportRefetchToggle]);

  const getInitialFilters = useCallback(() => {
    if (searchKeyword) {
      return initialFiltersValue;
    }

    if (role === UserRole.REGULAR || role === UserRole.CONTROLCENTER) {
      return {
        ...initialFiltersValue,
        reportsStatuses: statuses?.map((status) => status.id),
        ...(isFollowUp && {
          reportUpdateDatesRange: {
            startDate:
              role === UserRole.REGULAR ? thirtyDaysAgoDate : firstDate,
            endDate: todaysDate,
          },
        }),
      };
    }
    if (role === UserRole.SENIOR && !isFollowUp) {
      return {
        ...initialFiltersValue,
        reportsStatuses: statuses?.map((status) => status.id),
      };
    }
    return initialFiltersValue;
  }, [role, isFollowUp, statuses, searchKeyword]);

  const setInitialFilters = useCallback(() => {
    const initialFilterValues = getInitialFilters();
    setFilters(initialFilterValues);
    const formattedFilters = getFormattedFilters(initialFilterValues);
    setSimpleFilters(formattedFilters);
  }, [getInitialFilters]);

  useEffect(() => {
    if (statuses) {
      setIsFiltersRun(false);
      setInitialFilters();
      setCheckedReportsIds([]);
    }
  }, [getInitialFilters, setInitialFilters, statuses]);

  const title = () => {
    if (isFollowUp) {
      return role === UserRole.CONTROLCENTER
        ? t("allReports")
        : t("followUpReports");
    }
    return t("pendingReports");
  };

  const handleRunFilters = () => {
    const formattedFilters = getFormattedFilters(filters);
    setSimpleFilters(formattedFilters);
    setIsFiltersRun(true);
  };

  const handleClearFilters = () => {
    setInitialFilters();
    setIsFiltersRun(false);
  };

  const handlePageChange = (page: number) => {
    dispatch({ type: "reportsData/setPageNumberSearch", payload: page });
  };

  return (
    <PageContainer>
      {statuses && reportsPageData ? (
        <ReportsBase
          statuses={statuses}
          isLoading={isLoading || isFetching}
          isError={isError}
          reports={reportsPageData.listItems}
          title={title()}
          filters={filters}
          simpleFilters={simpleFilters}
          setFilters={setFilters}
          expandedFilters={expandedFilters}
          setExpandedFilters={setExpandedFilters}
          handleRunFilters={handleRunFilters}
          handleClearFilters={handleClearFilters}
          isFiltersRun={isFiltersRun}
          handlePageChange={handlePageChange}
          currentPage={pageNumber}
          totalReports={reportsPageData.totalPagedReports}
          totalPages={reportsPageData.pageCount}
          checkedReportsIds={checkedReportsIds}
          setCheckedReportsIds={setCheckedReportsIds}
          onReportRefetch={handleReportRefetch}
        />
      ) : (
        <Loading />
      )}
    </PageContainer>
  );
};

export default ReportsPage;
